import { AnyFunction, AnyObject } from "core/types";
import React, { useContext, useEffect, useState } from "react";
import de from "./locales/de";
import { default as Translations, default as TranslationsType } from "./types";

export type Locale = "de" | "en";

type TranslationsContextProps = {
  locale: Locale;
  translations: TranslationsType;
  updateLanguage: AnyFunction;
};

function mutateDevTranslations(
  translations: AnyFunction | AnyObject | string,
  path = "",
) {
  if (typeof translations !== "object" || !translations) return;
  for (const [key, value] of Object.entries(translations)) {
    const keyPath = path ? `${path}.${key}` : key;
    if (typeof value === "string") {
      translations[key] = keyPath;
    } else {
      mutateDevTranslations(value, keyPath);
    }
  }
}

async function setDevTranslations(
  setTranslations: (translations: TranslationsType) => void,
) {
  const { default: cloneDeep } = await import("lodash/cloneDeep");
  const devTranslations = cloneDeep(de);
  mutateDevTranslations(devTranslations);
  setTranslations(devTranslations);
}

const updateLanguage = async (
  language: string,
  setTranslations: (translations: TranslationsType) => void,
  setLocale: (locale: Locale) => void,
) => {
  if (language === "dev") {
    setDevTranslations(setTranslations);
    setLocale("en");
  } else if (language === "de") {
    setTranslations(de);
    setLocale("de");
  } else {
    import("./locales/en").then((file) => {
      setTranslations(file.default);
      setLocale("en");
    });
  }
};

export const TranslationsContext =
  React.createContext<TranslationsContextProps>({
    locale: "de",
    translations: de,
    updateLanguage,
  });

export function withTranslations<Outer extends AnyObject>(
  Component: React.ComponentType<
    Outer & { locale: Locale; translations: Translations }
  >,
) {
  return (props: Outer) => {
    const { locale, translations } =
      useContext<TranslationsContextProps>(TranslationsContext);

    return <Component {...props} locale={locale} translations={translations} />;
  };
}

export function AsyncTranslationsProvider({
  children,
  useGetLanguage,
}: {
  children: React.ReactNode;
  useGetLanguage?: () => string | undefined;
}) {
  const language = useGetLanguage?.();
  const [locale, setLocale] = useState<Locale>("de");
  const [translations, setTranslations] = useState<TranslationsType>(de);

  useEffect(() => {
    if (language) {
      updateLanguage(language, setTranslations, setLocale);
    }
  }, [language]);

  return (
    <TranslationsContext.Provider
      value={{
        locale,
        translations,
        updateLanguage: (locale: Locale | "dev") =>
          updateLanguage(locale, setTranslations, setLocale),
      }}
    >
      {children}
    </TranslationsContext.Provider>
  );
}

export function useLocale(): Locale {
  return useContext(TranslationsContext).locale;
}

export function useTranslations(): TranslationsType {
  return useContext(TranslationsContext).translations;
}

export function evaluateTranslation(
  translation: string | ((...args: unknown[]) => string),
  ...rest: unknown[]
) {
  return typeof translation === "function" ? translation(...rest) : translation;
}
